import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Image,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Divider,
  useToast,
  Link,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { UserAuth } from "../contexts/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";
import logo from "../assets/logos/logo.png";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import axios from "axios";
import { apiUrl } from "../config";
import { useNavigate } from "react-router-dom";

// Read a localized name that may be either { en, fr } or a plain string.
const displayName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en || name.fr || "";
};

const landingPathForInvite = (invite) => {
  if (!invite) return "/home";
  if (invite.type === "campaign" && invite.campaignId) {
    return `/campaign/${invite.campaignId}`;
  }
  if (invite.type === "organization" && invite.organizationId) {
    return `/organization/${invite.organizationId}`;
  }
  return "/home";
};

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const toast = useToast();
  const { user, signUpWithEmail, signInWithGoogle } = UserAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get("inviteId");
  const inviteToken = searchParams.get("token");

  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [acceptingInvite, setAcceptingInvite] = useState(false);

  // Fetch invite context (org/campaign name) for the header — no auth needed,
  // the token gates access.
  useEffect(() => {
    if (!inviteId || !inviteToken) return;
    let cancelled = false;
    setInviteLoading(true);
    axios
      .get(`${apiUrl}/pendingInvites/${inviteId}`, {
        params: { token: inviteToken },
      })
      .then((res) => {
        if (cancelled) return;
        setInvite(res.data);
        if (res.data.expired) {
          setInviteError("This invitation has expired. Please ask the sender for a new one.");
        } else if (res.data.status === "processed") {
          setInviteError("This invitation has already been used.");
        } else if (res.data.email) {
          setEmail(res.data.email);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404 || status === 403) {
          setInviteError("This invitation link is invalid.");
        } else {
          setInviteError("Could not load invitation details.");
        }
      })
      .finally(() => {
        if (!cancelled) setInviteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteId, inviteToken]);

  const acceptInvite = useCallback(async () => {
    const idToken = sessionStorage.getItem("idToken");
    const res = await axios.post(
      `${apiUrl}/acceptInvite`,
      { inviteId, token: inviteToken },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return res.data;
  }, [inviteId, inviteToken]);

  // If a logged-in user lands here with an invite link, process it immediately
  // and send them to the campaign/org instead of making them sign up again.
  useEffect(() => {
    if (!user || !inviteId || !inviteToken) return;
    if (inviteError || acceptingInvite) return;
    if (invite && (invite.expired || invite.status === "processed")) {
      navigate(landingPathForInvite(invite));
      return;
    }
    setAcceptingInvite(true);
    acceptInvite()
      .then((result) => {
        toast({
          title: "Invitation accepted",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        navigate(landingPathForInvite(result));
      })
      .catch((err) => {
        const data = err.response?.data || {};
        const title = data.error || "Invitation error";
        const description =
          data.message || "Could not accept invitation.";
        toast({
          title,
          description,
          status: err.response?.status === 409 ? "warning" : "error",
          duration: 7000,
          isClosable: true,
        });
        setAcceptingInvite(false);
      });
  }, [
    user,
    inviteId,
    inviteToken,
    invite,
    inviteError,
    acceptingInvite,
    acceptInvite,
    navigate,
    toast,
  ]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    if (!password) newErrors.password = "Password is required";
    if (password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegistrationAttempt = (registrationType, data = null) => {
    setPendingRegistration({ type: registrationType, data });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    handleRegistrationAttempt("email", {
      email,
      password,
      firstName,
      lastName,
    });
  };

  const processRegistration = async () => {
    if (!agreedToTerms || !pendingRegistration) return;

    setIsLoading(true);
    try {
      let userData = {};

      if (pendingRegistration.type === "email") {
        const { email, password, firstName, lastName } =
          pendingRegistration.data;
        userData = await signUpWithEmail(email, password, firstName, lastName);
      } else if (pendingRegistration.type === "google") {
        userData = await signInWithGoogle();
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));

      let redirectPath = "/home";

      if (inviteId && inviteToken && userData?.uid) {
        try {
          const result = await acceptInvite();
          redirectPath = landingPathForInvite(result);
        } catch (error) {
          console.error("Error accepting invite:", error);
          toast({
            title: "Note",
            description:
              "Account created, but the invitation could not be applied. Please contact support if needed.",
            status: "warning",
            duration: 7000,
            isClosable: true,
          });
        }
      }

      setIsModalOpen(false);

      // TEMP: email verification disabled
      navigate(redirectPath);
    } catch (error) {
      toast({
        title: "Registration failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setPendingRegistration(null);
      setAgreedToTerms(false);
    }
  };

  const inviteHeader = (() => {
    if (!inviteId) return null;
    if (inviteLoading) {
      return (
        <Alert status="info" borderRadius="md" mb={4}>
          <Spinner size="sm" mr={3} />
          <Text fontSize="sm">Loading invitation…</Text>
        </Alert>
      );
    }
    if (inviteError) {
      return (
        <Alert status="warning" borderRadius="md" mb={4}>
          <AlertIcon />
          <Text fontSize="sm">{inviteError}</Text>
        </Alert>
      );
    }
    if (!invite) return null;
    const target =
      invite.type === "campaign"
        ? displayName(invite.campaignName)
        : invite.organizationName;
    if (!target) return null;
    return (
      <Alert status="success" borderRadius="md" mb={4}>
        <AlertIcon />
        <Text fontSize="sm">
          You've been invited to join <strong>{target}</strong>. Create your
          account below to accept.
        </Text>
      </Alert>
    );
  })();

  if (user && inviteId && inviteToken && !inviteError) {
    return (
      <Box
        width="100%"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack>
          <Spinner size="lg" />
          <Text>Accepting your invitation…</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      minHeight="100vh"
      bg="primaryBackground"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={8}
    >
      <Stack align={"center"} textAlign={"center"} mb={8}>
        <Box as="button" cursor="pointer">
          <Image src={logo} alt="Logo" width={["200px", "250px"]} mb={8} />
        </Box>
        <Heading fontSize={"xl"} textAlign={"center"} color="heading">
          Create an account
        </Heading>
        <Text textAlign="center" color="text" fontSize="sm">
          Or{" "}
          <Link
            as={RouterLink}
            to="/"
            color="text"
            textDecoration={"underline"}
            // fontWeight="bold"
            _hover={{ textDecoration: "none" }}
          >
            sign in to your account
          </Link>
        </Text>
      </Stack>
      <Box
        maxWidth={["100%", "450px", "500px"]}
        width="100%"
        bg="secondaryBackground"
        p={[6, 8, 10]}
        borderRadius="xl"
        boxShadow="md"
      >
        {inviteHeader}
        <VStack align="stretch">
          <GoogleSignInButton
            text="Sign up with Google"
            onRegisterAttempt={() => handleRegistrationAttempt("google")}
          />

          <Flex align="center" pb={8}>
            <Divider borderColor="gray.300" />
            <Text px={2} color="gray.500" fontWeight="medium">
              or
            </Text>
            <Divider borderColor="gray.300" />
          </Flex>

          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <Box
                display="flex"
                flexDir={["column", "row"]}
                gap={4}
                width="100%"
              >
                <FormControl isInvalid={errors.firstName}>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.lastName}>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                </FormControl>
              </Box>

              <FormControl isInvalid={errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.password}>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <InputRightElement>
                    <IconButton
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>

              <Button
                mt={2}
                width="100%"
                variant={"secondary"}
                type="submit"
                isLoading={isLoading}
              >
                Register
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Terms of Service</ModalHeader>
          <ModalBody>
            <Text mb={4}>
              Please read and agree to our Terms of Service and Privacy Policy.
            </Text>
            <Checkbox
              isChecked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            >
              I agree to the Terms of Service and Privacy Policy
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={processRegistration}
              isDisabled={!agreedToTerms}
              isLoading={isLoading}
            >
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Register;
